/**
 * GUS Admin Portal Design System - Design Tokens
 * Unified design tokens for consistent UI across all components
 * Ported from GUS main application design system
 */

// Brand Colors - Primary accent colors used across the application
// GUS/SilaCities brand colors from logo gradient
export const brandColors = {
  // SilaCities brand palette (from logo gradient)
  silaBlue: '#3F589D',    // Start of SilaCities gradient
  silaTeal: '#32828B',    // Primary brand color - use for actions, buttons
  silaGreen: '#41A35F',   // End of gradient - use for indicators, success

  // Light mode variants
  silaTealLight: '#6fa7ad',
  silaGreenLight: '#67b57f',

  // Dark mode variants (lighter for visibility)
  silaTealDark: '#2d757d',
  silaGreenDark: '#3a9255',

  // Legacy colors (kept for backward compatibility)
  cyan: '#06b6d4',
  blue: '#3b82f6',
  purple: '#8b5cf6',
  teal: '#32828B',

  // Semantic colors
  success: '#41A35F',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#32828B',
} as const;

// Module-specific accent colors for visual differentiation
export const moduleColors = {
  atlas: '#17b8be',
  assessment: '#3b82f6',
  planning: '#10b981',
  gus: '#06b6d4',
  policy: '#a855f7',
} as const;

// Gradient presets - standardized gradients for headers, icons, buttons
export const gradients = {
  // SilaCities brand gradient (full 3-color)
  brand: 'linear-gradient(90deg, #3F589D, #32828B, #41A35F)',
  brandDark: 'linear-gradient(90deg, #6f81b5, #65a1a8, #70ba87)',

  // Primary gradient (teal to green) - main actions, send buttons
  primary: 'linear-gradient(135deg, #32828B, #41A35F)',

  // Success gradient (green shades) - positive states
  success: 'linear-gradient(135deg, #41A35F, #67b57f)',

  // Accent gradient (blue to teal) - special highlights
  accent: 'linear-gradient(135deg, #3F589D, #32828B)',

  // Subtle variants for backgrounds (10% opacity)
  subtlePrimary: 'linear-gradient(135deg, rgba(50,130,139,0.1) 0%, rgba(65,163,95,0.1) 100%)',
  subtleSuccess: 'linear-gradient(135deg, rgba(65,163,95,0.1) 0%, rgba(50,130,139,0.1) 100%)',
} as const;

// Glass morphism presets - standardized glass effects
export const glass = {
  // Standard blur value
  blur: 'blur(16px)',

  // Background colors by theme
  background: {
    dark: 'rgba(15, 23, 42, 0.6)',
    light: 'rgba(255, 255, 255, 0.8)',
  },

  // Border colors by theme
  border: {
    dark: 'rgba(148, 163, 184, 0.1)',
    light: 'rgba(0, 0, 0, 0.08)',
  },

  // Heavy glass (more opaque) for overlays
  heavy: {
    dark: 'rgba(15, 23, 42, 0.85)',
    light: 'rgba(255, 255, 255, 0.95)',
  },
} as const;

// Responsive breakpoints
export const breakpoints = {
  mobile: 767,
  tablet: 768,
  laptop: 1024,
  desktop: 1440,
  wide: 1680,
} as const;

// Media query helpers
export const media = {
  mobile: `@media (max-width: ${breakpoints.mobile}px)`,
  tablet: `@media (min-width: ${breakpoints.tablet}px)`,
  laptop: `@media (min-width: ${breakpoints.laptop}px)`,
  desktop: `@media (min-width: ${breakpoints.desktop}px)`,
  wide: `@media (min-width: ${breakpoints.wide}px)`,
  tabletOnly: `@media (min-width: ${breakpoints.tablet}px) and (max-width: ${breakpoints.laptop - 1}px)`,
  laptopOnly: `@media (min-width: ${breakpoints.laptop}px) and (max-width: ${breakpoints.desktop - 1}px)`,
} as const;

// Typography scale
export const typography = {
  fontFamily: {
    primary: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', 'Monaco', monospace",
  },
  fontSize: {
    xs: '0.6875rem',    // 11px
    sm: '0.8125rem',    // 13px
    base: '0.9375rem',  // 15px
    lg: '1.125rem',     // 18px
    xl: '1.5rem',       // 24px
    '2xl': '2rem',      // 32px
    '3xl': '2.5rem',    // 40px
  },
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const;

// Spacing scale
export const spacing = {
  '0': '0',
  '1': '4px',
  '1.5': '6px',
  '2': '8px',
  '2.5': '10px',
  '3': '12px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '8': '32px',
  '10': '40px',
  '12': '48px',
  '16': '64px',
} as const;

// Border radius scale
export const borderRadius = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '24px',
  full: '9999px',
} as const;

// Shadow scale
export const shadows = {
  sm: '0 2px 8px rgba(0, 0, 0, 0.08)',
  md: '0 4px 16px rgba(0, 0, 0, 0.12)',
  lg: '0 8px 32px rgba(0, 0, 0, 0.16)',
  xl: '0 12px 48px rgba(0, 0, 0, 0.2)',
  glow: {
    primary: '0 0 20px rgba(6, 182, 212, 0.15)',
    accent: '0 4px 12px rgba(59, 130, 246, 0.3)',
    success: '0 4px 12px rgba(16, 185, 129, 0.3)',
  },
} as const;

// Transition presets
export const transitions = {
  fast: '0.15s ease',
  normal: '0.25s ease',
  slow: '0.4s ease',
  color: 'color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
  transform: 'transform 0.2s ease',
  all: 'all 0.2s ease',
} as const;

// Z-index scale
export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  modal: 300,
  popover: 400,
  tooltip: 500,
  toast: 600,
} as const;

// Component-specific tokens
export const components = {
  header: {
    height: '72px',
    padding: '24px 32px',
    paddingCompact: '16px 24px',
  },
  sidebar: {
    width: '60px',
    widthExpanded: '240px',
  },
  card: {
    padding: '24px',
    paddingCompact: '16px',
    borderRadius: '16px',
  },
  input: {
    height: '40px',
    heightLarge: '48px',
    padding: '10px 16px',
    borderRadius: '8px',
  },
  button: {
    height: '40px',
    heightSmall: '32px',
    heightLarge: '48px',
    padding: '10px 20px',
    paddingSmall: '6px 12px',
    paddingLarge: '12px 24px',
    borderRadius: '8px',
  },
  tab: {
    height: '48px',
    padding: '16px 24px',
  },
} as const;

/**
 * Canonical GUS surface tokens.
 *
 * These mirror `silacities-gus-v2/design.md`, the normative design system for the
 * product this portal administers. They are additive on purpose: the `borderRadius`
 * and `typography` scales above predate GUS's "radius is 0" and Open-Sauce-Sans
 * decisions, and plenty of components still consume them. Changing those in place
 * would restyle the whole app; new and restyled surfaces reference these instead.
 */
export const canonical = {
  // design.md § Corner radius. Square reads as print; rounded reads as generic SaaS.
  // Roundness survives only where it IS the affordance.
  radius: {
    square: '0',    // --r-xs..--r-lg: every rectangle (card, panel, button, chip)
    field: '6px',   // --r-field: a rounded field reads as "type here"
    round: '50%',   // --r-round: true circles only (dots, avatars, spinners)
  },
  // design.md § Typography. GUS declares Open Sauce Sans but does not ship a
  // @font-face for it, so in practice this renders in the system fallback --
  // matching GUS means carrying the same stack, not adding a webfont.
  fontFamily: {
    body: '"Open Sauce Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  // Accent deepened for text-bearing fills: 5.3:1 with white, where the #32828B
  // accent itself is only 3.9:1. Use this behind white label text, not the accent.
  accentSolid: '#2D757D',
  greenSolid: '#2F7A46',
} as const;

// Export all tokens as a single object for convenience
export const tokens = {
  brandColors,
  moduleColors,
  gradients,
  glass,
  canonical,
  breakpoints,
  media,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  components,
} as const;

export default tokens;
